import { supportsLightChannels, lightPhaseValuesKnown, phaseLightErrors, lightCycle } from './light_profiles';
import _ from 'lodash';
import { isSchedulingUiEnabled, supportsScheduleProtocol, validScheduleNames, scheduleShortNameLimit, transformScheduleValuesForSend, schedulingSettingsReady } from './scheduling_utils';
import { hasInvalidNutrientSettingsUnit } from './nutrition_utils';
import { persistSettingsDraft } from './settings_draft';

export const SCHEDULE_SUBMISSION_TIMEOUT_MS = 60000;

export function persistScheduleDraft(parent) {
  persistSettingsDraft(parent);
}

// A split readback is not a competing save. Only a complete recipe can make
// retained settings stale; submission reconciliation still checks its content.
export function scheduleDraftSubmission(parent) {
  const submission = parent.schedule_submission;
  if (['pending', 'rejected', 'unconfirmed', 'unsent'].includes(submission?.status)) return submission;
  const baseline = parent.settings?.scheduling;
  const retained = parent.temp_settings?.scheduling;
  if (!retained || !schedulingSettingsReady(baseline)) return submission;
  const revision = baseline.schedule_values.recipe_revision;
  const draftRevision = retained.schedule_values?.recipe_revision ?? retained.schedule_meta?.recipe_revision;
  const ownConfirmation = submission?.status === 'confirmed' &&
    submission.expected_revision === revision && draftRevision === submission.base_revision;
  return draftRevision !== revision && !ownConfirmation ? { status: 'conflict' } : submission;
}

export function discardScheduleDraft(parent) {
  delete parent.temp_settings.scheduling;
  delete parent.schedule_submission;
  parent.schedule_draft_loaded = true;
  persistScheduleDraft(parent);
  return true;
}

export function keepScheduleDraft(parent) {
  if (parent.schedule_submission?.status === 'pending') return false;
  const baseline = parent.settings.scheduling;
  if (!schedulingSettingsReady(baseline)) return false;
  const retained = parent.temp_settings.scheduling;
  if (retained?._editing_phase && !baseline.schedule_values.scheduling_values.some(
    (phase) => phase.phase_id === retained._editing_phase.phase_id
  )) return false;
  if (retained) {
    const next = _.cloneDeep(retained);
    const revision = baseline.schedule_values.recipe_revision;
    next.schedule_meta.recipe_revision = revision;
    next.schedule_values.recipe_revision = revision;
    if (next._editing_phase) next._editing_phase.recipe_revision = revision;
    parent.temp_settings.scheduling = next;
  }
  parent.schedule_draft_loaded = true;
  parent.schedule_submission = { status: 'review' };
  persistScheduleDraft(parent);
  return true;
}

export function beginScheduleSubmission(
  parent,
  values,
  baseRevision,
  now = Date.now(),
  wireValues = values
) {
  // Retain the complete submitted editor state across disconnects/navigation.
  parent.temp_settings.scheduling = _.cloneDeep(values);
  persistScheduleDraft(parent);
  parent.schedule_submission = {
    status: 'pending',
    base_revision: baseRevision,
    expected_revision: (baseRevision + 1) >>> 0 || 1,
    started_at: now,
    submitted_draft: _.cloneDeep(values),
    submitted_wire: _.cloneDeep(wireValues),
    metadata_received: false,
    phases_received: false
  };
}

export function expireScheduleSubmission(parent, now = Date.now()) {
  const pending = parent.schedule_submission;
  if (pending?.status === 'pending' && now - pending.started_at >= SCHEDULE_SUBMISSION_TIMEOUT_MS) {
    pending.status = 'unconfirmed';
  }
}

export function rejectScheduleSubmission(controller, message) {
  const parts = message?.message_parts;
  if (!Array.isArray(parts) || parts[0] !== 'schedule_upload_failed') return false;
  const zone = Number(parts[1]);
  const parent = controller?.zones ? controller.zones[zone] : controller;
  const pending = parent?.schedule_submission;
  if (
    pending &&
    ['pending', 'unconfirmed', 'rejected'].includes(pending.status) &&
    Number(parts[2]) === pending.base_revision
  ) {
    pending.status = 'rejected';
    pending.reason = parts[3];
  }
  return true;
}

// Use the same failure explanation in the page, header and controller reply.
// A refused ESP upload is distinct from SAM rejecting the submitted settings.
export function scheduleSubmissionMessage(submission) {
  if (submission.status !== 'rejected' || !submission.reason || submission.reason === 'controller_rejected')
    return `schedule_submission_${submission.status}`;
  switch (submission.reason) {
    case 'busy':
    case 'queue_full': return 'settings_upload_busy';
    case 'awaiting_readback':
    case 'missing_readback':
    case 'missing_zone': return 'settings_upload_awaiting_readback';
    case 'stale_base': return 'settings_upload_stale_base';
    case 'incompatible_recipe':
    case 'incompatible_readback': return 'settings_upload_incompatible';
    case 'missing_recipe':
    case 'invalid_phase_count':
    case 'incomplete_recipe': return 'settings_upload_incomplete';
    case 'allocation_failed': return 'settings_upload_allocation_failed';
    case 'timeout': return 'settings_upload_timeout';
    default: return 'settings_upload_rejected';
  }
}

const ignoredFields = new Set([
  'recipe_revision',
  'base_recipe_revision',
  'active_recipe_revision',
  'runtime_status',
  'active_phase_index',
  'recipe_valid',
  'zone',
  'id'
]);

// Compare the actual editable payload, not the revision alone: another client
// can win the same base+1 revision. The DTO may add defaults and normalize
// boolean flags to 0/1. Extra read-only/default fields are not user edits.
export function schedulePayloadMatches(expected, actual) {
  if (expected === undefined) return true;
  if (Array.isArray(expected)) {
    return (
      Array.isArray(actual) &&
      expected.length === actual.length &&
      expected.every((value, index) => schedulePayloadMatches(value, actual[index]))
    );
  }
  if (expected && typeof expected === 'object') {
    return (
      actual != null &&
      Object.entries(expected).every(
        ([key, value]) =>
          ignoredFields.has(key) ||
          key.startsWith('_') ||
          schedulePayloadMatches(value, actual[key])
      )
    );
  }
  if (typeof expected === 'boolean' || typeof actual === 'boolean') {
    return (
      [true, false, 0, 1].includes(expected) &&
      [true, false, 0, 1].includes(actual) &&
      Number(expected) === Number(actual)
    );
  }
  return expected === actual;
}

export function reconcileScheduleSubmission(parent, data) {
  const pending = parent.schedule_submission;
  if (!pending || !['pending', 'unconfirmed', 'rejected', 'conflict'].includes(pending.status))
    return;
  const revision = pending.expected_revision;
  if (Number(data.schedule_meta?.recipe_revision) === revision) {
    pending.metadata_received = true;
  }
  const recipe = data.schedule_values;
  if (
    Number(recipe?.recipe_revision) === revision &&
    Array.isArray(recipe.scheduling_values) &&
    recipe.scheduling_values.length === Number(recipe.schedule_values_count)
  ) {
    pending.phases_received = true;
  }
  const baseline = parent.settings.scheduling;
  if (
    !pending.metadata_received ||
    !pending.phases_received ||
    Number(baseline?.schedule_meta?.recipe_revision) !== revision ||
    Number(baseline?.schedule_values?.recipe_revision) !== revision ||
    Number(baseline?.schedule_meta?.active_phase_cnt) !==
      Number(baseline?.schedule_values?.schedule_values_count)
  )
    return;

  // Failure broadcasts identify a base revision shared by concurrent clients.
  // Complete authoritative content takes precedence over that ambiguous failure.
  delete pending.reason;
  if (!schedulePayloadMatches(pending.submitted_wire, baseline)) {
    pending.status = 'conflict';
    return;
  }

  // Changes made while the upload was in flight remain an unsaved draft. With
  // no subsequent edits, display the controller's normalized committed values.
  if (_.isEqual(parent.temp_settings.scheduling, pending.submitted_draft)) {
    delete parent.temp_settings.scheduling;
    persistScheduleDraft(parent);
  }
  pending.status = 'confirmed';
}

/** Submit the metadata and all phases as one revisioned transaction. A true
 * result confirms socket submission only, not controller acceptance. */
export function submitScheduleSettings({ values, parent, controller, stateManager, tab_id }) {
  if (!isSchedulingUiEnabled(stateManager.previewFeaturesEnabled)) return false;
  if (!controller.access_rights?.scheduling?.write) return false;
  if (parent.schedule_submission?.status === 'pending') return false;
  if (!supportsScheduleProtocol(controller, values)) return false;
  if (values.schedule_values.scheduling_values.some(
    (phase) => !phase._deleted && hasInvalidNutrientSettingsUnit(phase.nutrient_settings)
  )) return false;
  if (!validScheduleNames(values, scheduleShortNameLimit(controller, values))) return false;
  if (supportsLightChannels(controller)) {
    const installations = controller.settings.light_channels.channels.filter(
      (c) => c.zone === Number(parent.zone_id)
    );
    for (const phase of values.schedule_values?.scheduling_values ?? []) {
      if (phase._deleted) continue;
      const previous = parent.settings?.scheduling?.schedule_values?.scheduling_values?.find(
        (p) => p.phase_id === phase.phase_id
      );
      if (!lightPhaseValuesKnown(phase.lighting) || !lightPhaseValuesKnown(previous?.lighting))
        return false;
      const problems = phaseLightErrors(
        phase.lighting,
        lightCycle(
          parent.settings.timing,
          values.schedule_meta.schedule_day_night_enabled ? phase : null
        ),
        installations,
        previous?.lighting
      );
      if (problems.length) return false;
    }
  }

  const protocolVersion = values.schedule_meta.protocol_version;
  const scheduleValues = _.cloneDeep(values.schedule_values);
  const retainedIndexes = scheduleValues.scheduling_values
    .map((phase, index) => (!phase._deleted ? index : null))
    .filter((index) => index !== null);
  scheduleValues.scheduling_values = retainedIndexes.map(
    (index) => scheduleValues.scheduling_values[index]
  );
  if (!scheduleValues.scheduling_values.length) return false;
  const recipe = transformScheduleValuesForSend({ ...scheduleValues, protocol_version: protocolVersion });
  const currentRevision = Number(
    parent.settings?.scheduling?.schedule_values?.recipe_revision ??
      parent.settings?.scheduling?.schedule_meta?.recipe_revision ??
      0
  );
  recipe.base_recipe_revision = currentRevision;
  recipe.recipe_revision = currentRevision;

  const scheduleMeta = {
    ..._.cloneDeep(values.schedule_meta),
    protocol_version: protocolVersion,
    recipe_revision: currentRevision,
    active_phase_cnt: recipe.schedule_values_count
  };
  // Phase durations are stored by position. Deleting a middle phase must move
  // the following duration together with its phase values in the transaction.
  retainedIndexes.forEach((originalIndex, index) => {
    scheduleMeta[`len_phase_${index + 1}_days`] =
      values.schedule_meta[`len_phase_${originalIndex + 1}_days`];
  });
  if (controller.connection.status !== 'good') return false;
  const settingsData = {
    schedule_meta: scheduleMeta,
    schedule_values: recipe
  };
  const zoneable = controller.type === 6000 || controller.type === 5155;
  if (zoneable) settingsData.zone = parent.zone_id;

  try {
    if (
      !stateManager.wsManager.sendWSMsg(
        controller,
        'update_setpoints',
        settingsData,
        controller.type === 6000,
        zoneable
      )
    ) {
      parent.schedule_submission = { status: 'unsent' };
      return false;
    }
  } catch (error) {
    parent.schedule_submission = { status: 'unsent' };
    console.error('[scheduling] Failed to submit settings', error);
    return false;
  }

  beginScheduleSubmission(parent, values, currentRevision, Date.now(), settingsData);
  return true;
}
